import Vue from 'vue'
import * as types from 'store/mutation-types'
import Option from 'utils/option'
import Stock from 'utils/stock'
import { isNewQuote } from 'utils/common'
import choiceFilter from 'utils/choiceFilter'
import isNeedRefresh from 'utils/isNeedRefresh'
import { SEC_TYPE } from 'const/stock'
import { fetchStockInfoDetails } from 'apis/stock'
import bus from 'src/eventbus'
import isSameStock from 'utils/isSameStock'
import getLocalFixedQuoteDetail from 'utils/getLocalFixedQuoteDetail'

const ACTIVE_STOCKS_COUNT = 9
const state = {
  tradeStocks: {
    active: null,
    stocks: []
  }
}
const getters = {
  tradeStocks: state => {
    if (!state.tradeStocks.stocks || state.tradeStocks.stocks.length === 0) {
      return []
    }
    return state.tradeStocks.stocks.filter(Boolean)
  },
  activeTradeStockIndex: state => state.tradeStocks.active,
  activeStock: (state, getters) => {
    if (
      state.tradeStocks.stocks.length > 0 &&
      state.tradeStocks.active != null
    ) {
      const stock = getters.tradeStocks[state.tradeStocks.active]
      if (!stock) {
        throw new Error('get active stock error')
      }
      return {
        ...stock,
        digits: choiceFilter(stock)
      }
    }
  },
  activeStockMark: (state, getters) => {
    const stock = getters.activeStock
    if (!stock || !stock.webLocal) {
      return null
    }
    return stock.webLocal.symbol
  },
  activeStockNeedRefresh: (state, getters) => {
    const stock = getters.activeStock
    if (!stock) {
      return false
    }
    return isNeedRefresh(stock, getters)
  }
}
const mutations = {
  [types.ADD_TRADE_STOCK](
    state,
    [stock, multiviewLockType, multiviewLockedData]
  ) {
    const addedStockExistLocation = state.tradeStocks.stocks.findIndex(
      tradeStock => isSameStock(tradeStock, stock)
    )
    if (addedStockExistLocation >= 0) {
      Object.assign(state.tradeStocks.stocks[addedStockExistLocation], stock)
      state.tradeStocks.active = addedStockExistLocation
      return
    }
    // limit tab count to 10
    const stocks = state.tradeStocks.stocks
    if (stocks.length >= ACTIVE_STOCKS_COUNT) {
      // remove the earliest added stock
      const multiviewData =
        multiviewLockedData && multiviewLockedData[multiviewLockType]
      if (
        multiviewLockType !== 1 &&
        multiviewData &&
        multiviewData.length > 0
      ) {
        const firstUnlockSymbol = stocks.findIndex((item, index) => {
          return multiviewData.every(viewItem => {
            return viewItem
              ? viewItem.webLocal.symbol !== item.webLocal.symbol
              : true
          })
        })
        stocks.splice(firstUnlockSymbol, 1)
      } else {
        stocks.splice(stocks.length - 1, 1)
      }
    }
    state.tradeStocks.stocks.unshift(stock)
    state.tradeStocks.active = 0
  },
  [types.RESTORE_STOCKS](state, data) {
    state.tradeStocks.active = data.active
    state.tradeStocks.stocks = data.stocks || []
    state.tradeStocks.stocks.forEach(stock => {
      delete stock.hourTrading
    })
  },
  [types.DELETE_TRADE_STOCK](state, stock) {
    const tradeStocks = state.tradeStocks.stocks
    const activeIndex = state.tradeStocks.active
    if (tradeStocks.length === 1) return
    const index = tradeStocks.findIndex(tradeStock =>
      isSameStock(tradeStock, stock)
    )
    if (index < 0) return
    tradeStocks.splice(index, 1)
    if (index < activeIndex) {
      state.tradeStocks.active -= 1
    }
    if (activeIndex >= tradeStocks.length) {
      state.tradeStocks.active = tradeStocks.length - 1
    }
  },
  [types.ACTIVE_TRADE_STOCK](state, stock) {
    const index = state.tradeStocks.stocks.findIndex(item =>
      isSameStock(item, stock)
    )
    if (index >= 0) {
      state.tradeStocks.active = index
    }
  },
  [types.REFRESH_ACTIVE_STOCK_DETAIL](state, stock) {
    const index = state.tradeStocks.active
    const curStock = state.tradeStocks.stocks[index]
    if (index >= 0 && isNewQuote(curStock, stock)) {
      delete curStock.hourTrading
      Vue.set(state.tradeStocks.stocks, index, {
        ...curStock,
        ...stock
      })
    }
  },
  [types.REFRESH_STOCK_DETAIL](state, stock) {
    const index = state.tradeStocks.stocks.findIndex(tradeStock =>
      isSameStock(tradeStock, stock)
    )
    if (index >= 0) {
      delete state.tradeStocks.stocks[index].hourTrading
      Vue.set(state.tradeStocks.stocks, index, {
        ...state.tradeStocks.stocks[index],
        ...stock
      })
    }
  },
  [types.CLEAR_TRADE_STOCKS](state) {
    state.tradeStocks.stocks = []
    state.tradeStocks.active = 0
  }
}

const actions = {
  addTradeStock({ commit, dispatch }, payload) {
    const stock = getLocalFixedQuoteDetail(payload)
    stock.symbol =
      stock.currentTab === 'holding' && stock.secType !== SEC_TYPE.STK
        ? stock.localSymbol
        : stock.symbol
    if (stock.type) stock.stockType = stock.type
    commit(types.ADD_TRADE_STOCK, [
      stock,
      getters.multiviewLockType,
      getters.multiviewLockedData
    ])
    dispatch('refreshOneStock', stock)
  },
  addOrActivateTradeStock({ commit, getters, dispatch }, stock) {
    const target = getters.tradeStocks.find(item => isSameStock(item, stock))
    if (target) {
      return dispatch('activateStock', target)
    }
    if (stock.secType !== SEC_TYPE.OPT) {
      return dispatch('addTradeStock', stock)
    }
    if (stock.secType === SEC_TYPE.OPT) {
      return dispatch('addOption', stock)
    }
    return Promise.reject(new Error('No Stock'))
  },
  deleteTradeStock({ commit }, stock) {
    commit(types.DELETE_TRADE_STOCK, stock)
  },
  activateStock({ commit, dispatch }, stock) {
    commit(types.ACTIVE_TRADE_STOCK, stock)
    return Promise.resolve(stock)
  },
  refreshTradeStocksName({ commit, state }) {
    const items = []
    const { stocks } = state.tradeStocks
    stocks.forEach(stock => {
      stock.secType === SEC_TYPE.STK &&
        items.push({
          symbol: stock.symbol
        })
    })
    Stock.getAllStockBriefs({ items }).then(respData => {
      respData.map(stock => {
        commit(types.REFRESH_STOCK_DETAIL, stock)
      })
    })
  },
  refreshOneStock({ dispatch }, stock) {
    if (!stock) return Promise.resolve()
    if (stock.secType !== SEC_TYPE.OPT) {
      return Stock.getStock(stock.market, stock.symbol)
        .getDetail()
        .then(item => {
          dispatch('whenStockUpdate', item)
          bus.emit(bus.$events.STOCK_UPDATE, stock)
          return item
        })
    }
    return new Option(stock.symbol, stock.expiry, stock.strike, stock.right)
      .getDetail()
      .then(option => {
        let askBidObj = {}
        // 已打开期权重置买卖盘
        if (stock.needRestAskBid) {
          askBidObj = {
            askPrice: undefined,
            askSize: undefined,
            bidPrice: undefined,
            bidSize: undefined
          }
        }
        const updateOption = { ...askBidObj, ...option }
        dispatch('whenStockUpdate', updateOption)
        bus.emit(bus.$events.STOCK_UPDATE, updateOption)
        return option
      })
  },
  refreshMultiStock({ commit }, { market, items }) {
    if (!items || items.length === 0) return Promise.resolve()
    const symbols = items.map(symbol => ({ symbol: symbol }))
    return fetchStockInfoDetails(market, symbols).then(response => {
      if (!response.data || !response.data.items) return Promise.resolve()
      const items = response.data.items
      items.forEach(item => {
        commit(types.REFRESH_STOCK_DETAIL, getLocalFixedQuoteDetail(item))
      })
      return items
    })
  },
  addOption({ commit, dispatch }, detail) {
    detail = getLocalFixedQuoteDetail(detail)
    commit(types.ADD_TRADE_STOCK, [
      detail,
      getters.multiviewLockType,
      getters.multiviewLockedData
    ])
    dispatch('refreshOneStock', detail)
  },
  whenStockUpdate({ commit, getters }, stock) {
    if (getters.multiviewLockType === 1) {
      if (isSameStock(getters.activeStock, stock)) {
        commit(types.REFRESH_ACTIVE_STOCK_DETAIL, stock)
      }
    } else {
      commit(types.REFRESH_STOCK_DETAIL, stock)
    }
  }
}
export default { state, getters, mutations, actions }
